import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { processImages, initializeModel, getModelInfo } from "../lib/process";
import { applyBatchConfig, BatchConfig } from "../lib/composite";
import { EditModal } from "./components/EditModal";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Loader2,
  Upload,
  Wand2,
  Trash2,
  Cpu,
  Download,
  CheckCircle2,
  ChevronRight,
  Settings2,
  Maximize2,
  RefreshCw,
  Clock,
  CheckCircle,
  Image as ImageIcon,
  Edit2,
  Languages,
  Brush
} from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { CompareSlider } from "./components/Images";
import { SidebarItem } from "./components/SidebarItem";

interface AppError {
  message: string;
}

export interface ImageFile {
  id: number;
  file: File;
  aiMaskFile?: File; // Store the transparent result for quick recomposition
  processedFile?: File;
  status: 'pending' | 'processing' | 'done' | 'approved';
  originalUrl: string;
  processedUrl?: string;
}

const predefinedColors = [
  '#ffffff', '#000000', '#F44336', '#4CAF50', '#2196F3',
  '#FFEB3B', '#00BCD4', '#E91E63', '#9E9E9E', '#607D8B'
];

export default function App() {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AppError | null>(null);
  const [isWebGPU, setIsWebGPU] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [currentModel, setCurrentModel] = useState<'briaai/RMBG-1.4' | 'Xenova/modnet'>('briaai/RMBG-1.4');
  const [isModelSwitching, setIsModelSwitching] = useState(false);

  // New Studio State Management
  const [images, setImages] = useState<ImageFile[]>([]);
  const [checkingIdx, setCheckingIdx] = useState<number | null>(null);
  const [uploadMode, setUploadMode] = useState<'files' | 'folder'>('files');
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Batch Config states (Keep all functionality)
  const [batchBgType, setBatchBgType] = useState<'transparent' | 'color' | 'image'>('transparent');
  const [batchBgColor, setBatchBgColor] = useState('#ffffff');
  const [batchBgImage, setBatchBgImage] = useState<File | null>(null);
  const [batchBlur, setBatchBlur] = useState(0);

  useEffect(() => {
    const { isIOS: isIOSDevice, isWebGPUSupported } = getModelInfo();
    setIsIOS(isIOSDevice);
    setIsWebGPU(isWebGPUSupported);
    setIsLoading(false);
  }, []);

  // 1. AI Queue Processor: ONLY handles background removal
  useEffect(() => {
    const processNext = async () => {
      // Check if any image is already being processed to avoid overlaps
      if (images.some(img => img.status === 'processing')) return;

      const nextPending = images.find(img => img.status === 'pending');
      if (!nextPending) return;

      setImages(prev => prev.map(img =>
        img.id === nextPending.id ? { ...img, status: 'processing' as const } : img
      ));

      try {
        const aiProcessed = await processImages([nextPending.file]);
        if (!aiProcessed?.[0]) throw new Error("AI failed");

        setImages(prev => prev.map(img =>
          img.id === nextPending.id
            ? { ...img, status: 'done' as const, aiMaskFile: aiProcessed[0] }
            : img
        ));

        if (checkingIdx === null) {
          setCheckingIdx(nextPending.id);
        }
      } catch (err) {
        console.error(err);
        setImages(prev => prev.filter(img => img.id !== nextPending.id));
      }
    };
    processNext();
  }, [images.filter(img => img.status === 'pending').length, images.some(img => img.status === 'processing')]);

  // 2. Composition Engine: Updates images whenever presets change
  const lastConfigRef = useRef<string>("");

  useEffect(() => {
    const applyPresets = async () => {
      const config: BatchConfig = {
        bgType: batchBgType,
        bgColor: batchBgColor,
        bgImage: batchBgImage,
        blur: batchBlur
      };

      const configKey = JSON.stringify({ batchBgType, batchBgColor, bgImg: batchBgImage?.name, batchBlur });
      const configChanged = lastConfigRef.current !== configKey;
      lastConfigRef.current = configKey;

      // Find images that need processing: 
      // - Have an AI mask 
      // - AND (Config changed OR don't have a processed result yet)
      const imagesToProcess = images.filter(img =>
        img.aiMaskFile && (configChanged || !img.processedUrl)
      );

      if (imagesToProcess.length === 0) return;

      for (const img of imagesToProcess) {
        try {
          const resultFile = await applyBatchConfig(img.file, img.aiMaskFile!, config);
          const newUrl = URL.createObjectURL(resultFile);

          setImages(prev => prev.map(item => {
            if (item.id === img.id) {
              if (item.processedUrl) URL.revokeObjectURL(item.processedUrl);
              return { ...item, processedFile: resultFile, processedUrl: newUrl };
            }
            return item;
          }));
        } catch (e) {
          console.error("Composition Error:", e);
        }
      }
    };
    applyPresets();
  }, [batchBgType, batchBgColor, batchBgImage, batchBlur, images.filter(i => !!i.aiMaskFile).length]);

  const handleModelChange = async (val: string) => {
    const newModel = val as typeof currentModel;
    setIsModelSwitching(true);
    setError(null);
    try {
      const initialized = await initializeModel(newModel);
      if (!initialized) throw new Error("Initialization failed");
      setCurrentModel(newModel);
    } catch (err) {
      if (err instanceof Error && err.message.includes("Falling back")) {
        setCurrentModel('briaai/RMBG-1.4');
      } else {
        setError({ message: err instanceof Error ? err.message : t('model.engine_error') });
      }
    } finally {
      setIsModelSwitching(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const timestamp = Date.now();
    const newImages: ImageFile[] = acceptedFiles.map((file, index) => ({
      id: timestamp + index,
      file,
      status: 'pending',
      originalUrl: URL.createObjectURL(file)
    }));

    // Auto-select the first new image if we don't have a valid selection currently
    const isSelectionValid = checkingIdx !== null && images.some(img => img.id === checkingIdx);
    if (!isSelectionValid) {
      setCheckingIdx(newImages[0].id);
    }

    setImages(prev => [...prev, ...newImages]);

    try {
      await initializeModel(currentModel);
    } catch (err) {
      setError({ message: t('model.engine_error') });
    }
  }, [currentModel, checkingIdx, images, t]);

  const handleApprove = (id: number) => {
    let nextDoneId: number | null = null;

    setImages(prev => {
      const updated = prev.map(img =>
        img.id === id ? { ...img, status: 'approved' as const } : img
      );

      // Find the next available 'done' image to inspect
      const nextDone = updated.find(img => img.status === 'done' && img.id !== id);
      nextDoneId = nextDone ? nextDone.id : null;

      return updated;
    });

    setCheckingIdx(nextDoneId);
  };

  const handleReprocess = (id: number) => {
    let nextDoneId: number | null = null;

    setImages(prev => {
      const updated = prev.map(img =>
        img.id === id ? { ...img, status: 'pending' as const, processedFile: undefined, processedUrl: undefined, aiMaskFile: undefined } : img
      );

      // If we are reprocessing the image currently being checked, find another 'done' one to show
      if (checkingIdx === id) {
        const nextDone = updated.find(img => img.status === 'done' && img.id !== id);
        nextDoneId = nextDone ? nextDone.id : null;
      } else {
        nextDoneId = checkingIdx; // Stay on current if we reprocess from approved list
      }

      return updated;
    });

    setCheckingIdx(nextDoneId);
  };

  const downloadAllAsZip = async () => {
    const zip = new JSZip();
    const approved = images.filter(img => img.status === 'approved' && img.processedFile);
    if (approved.length === 0) return;

    approved.forEach((img) => {
      zip.file(img.processedFile!.name, img.processedFile!);
    });

    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "bg-removed-studio.zip");
  };

  const handleFolderUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Detection for common image types including those with missing MIME types
    const imageFiles = files.filter(file => {
      const isImageMime = file.type.startsWith('image/');
      const isImageExt = /\.(jpg|jpeg|png|webp|avif|heic|bmp)$/i.test(file.name);
      return isImageMime || isImageExt;
    });

    if (imageFiles.length > 0) {
      onDrop(imageFiles);
    } else {
      console.warn("No valid images found in the selected folder");
    }

    // Reset so the same folder can be re-selected if needed
    e.target.value = '';
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".jpeg", ".jpg", ".png", ".webp"] },
    multiple: true,
    noClick: uploadMode === 'folder'
  });

  const currentCheckingImage = images.find(img => img.id === checkingIdx);

  return (
    <div className="h-screen flex flex-col bg-[#F6F8FA] dark:bg-[#050505] overflow-hidden">
      <header className="h-14 border-b bg-white dark:bg-[#0a0a0a] flex items-center justify-between px-6 z-50 shrink-0 mx-auto w-full">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Brush className="h-5 w-5 text-primary" />
            <span className="font-black italic tracking-tighter uppercase text-sm leading-none flex items-center gap-1">
              {t('app.title')} <span className="text-[9px] font-bold non-italic opacity-40">{t('app.subtitle')}</span>
            </span>
          </div>
          <div className="h-4 w-px bg-border mx-2" />
        </div >

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-3 py-1 bg-muted/50 rounded-full border border-border/50">
            <Languages className="h-3 w-3 text-muted-foreground" />
            <Select value={i18n.language} onValueChange={(val) => i18n.changeLanguage(val)}>
              <SelectTrigger className="h-6 w-[80px] text-[8px] font-black uppercase border-none bg-transparent shadow-none focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vi" className="text-[10px] font-bold">Tiếng Việt</SelectItem>
                <SelectItem value="en" className="text-[10px] font-bold">English</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 px-3 py-1 bg-muted/50 rounded-full border border-border/50">
            <Cpu className="h-3 w-3 text-muted-foreground" />
            <Select value={currentModel} onValueChange={handleModelChange} disabled={!isWebGPU || isLoading || isModelSwitching}>
              <SelectTrigger className="h-6 w-[120px] text-[8px] font-black uppercase border-none bg-transparent shadow-none focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="briaai/RMBG-1.4" className="text-[10px] font-bold">{t('model.gpu_fast')}</SelectItem>
                {isWebGPU && <SelectItem value="Xenova/modnet" className="text-[10px] font-bold">{t('model.modnet_gpu')}</SelectItem>}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header >

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar: Production Queue */}
        <aside className="w-[300px] border-r bg-white dark:bg-[#0a0a0a] flex flex-col shrink-0">
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-[10px] font-black uppercase tracking-widest">{t('upload.production_queue')} • {images.filter(i => i.status !== 'approved').length}</span>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 opacity-50 hover:opacity-100" onClick={() => setImages([])}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Mode Selector - Moved here */}
            <Tabs value={uploadMode} onValueChange={(v) => setUploadMode(v as any)} className="w-full">
              <TabsList className="grid grid-cols-2 bg-muted/50 p-1 h-10 rounded-xl border-none">
                <TabsTrigger value="files" className="rounded-lg text-[9px] font-black uppercase tracking-widest data-[state=active]:bg-white data-[state=active]:shadow-sm">{t('upload.individual')}</TabsTrigger>
                <TabsTrigger value="folder" className="rounded-lg text-[9px] font-black uppercase tracking-widest data-[state=active]:bg-white data-[state=active]:shadow-sm">{t('upload.folder')}</TabsTrigger>
              </TabsList>
            </Tabs>

            <div
              {...getRootProps({
                onClick: (e) => {
                  if (uploadMode === 'folder') {
                    // Stop dropzone from opening file dialog
                    e.stopPropagation();
                    // Open our custom folder dialog
                    folderInputRef.current?.click();
                  }
                  // Otherwise, dropzone's rootProps will handle it normally
                }
              })}
              className={`h-28 border-2 border-dashed rounded-[1.5rem] flex flex-col items-center justify-center p-4 text-center gap-2 group cursor-pointer transition-all ${isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30 hover:bg-primary/[0.02]'}`}
            >
              <input {...getInputProps()} />
              <input
                type="file"
                ref={folderInputRef}
                className="hidden"
                multiple
                {...({ webkitdirectory: "", directory: "" } as any)}
                onChange={handleFolderUpload}
              />
              <div className="h-8 w-8 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary transition-colors">
                <Upload className="h-4 w-4 text-primary group-hover:text-white" />
              </div>
              <p className="text-[9px] font-black uppercase tracking-widest">{uploadMode === 'files' ? t('upload.drop_images') : t('upload.choose_folder')}</p>
            </div>

            {images.filter(img => img.status !== 'approved').map(img => (
              <SidebarItem
                key={img.id}
                image={img}
                isActive={checkingIdx === img.id}
                onClick={() => img.status === 'done' && setCheckingIdx(img.id)}
                variant="queue"
              />
            ))}
          </div>
        </aside>

        {/* Center: Workspace */}
        <main className="flex-1 flex flex-col relative bg-[#F0F2F5] dark:bg-black/20 overflow-hidden">
          {/* Review Bar - Only show if there are done images */}
          <div className="w-full shrink-0 bg-transparent px-6 h-20 flex items-center gap-6 overflow-x-auto no-scrollbar border-b border-border/30 animate-in slide-in-from-top duration-300">
            <div className="flex flex-col border-r border-border/50 pr-6 shrink-0">
              <span className="text-[9px] font-black uppercase tracking-[0.2em] text-primary/40">{t('upload.ready_to_review')}</span>
              <span className="text-[16px] font-black tabular-nums opacity-60">{images.filter(i => i.status === 'done').length}</span>
            </div>

            <div className="flex items-center gap-3 h-full py-3">
              {images.filter(i => i.status === 'done').map(img => (
                <div
                  key={img.id}
                  onClick={() => setCheckingIdx(img.id)}
                  className={`h-full aspect-[4/3] rounded-lg border-2 cursor-pointer transition-all overflow-hidden flex items-center justify-center relative bg-checkered
                              ${checkingIdx === img.id ? 'border-primary ring-4 ring-primary/10 shadow-md' : 'border-transparent hover:border-primary/30'}
                          `}
                  style={{ backgroundSize: '10px 10px' }} // Smaller checker for mini preview
                >
                  <img src={img.processedUrl} className="h-full w-full object-contain p-1.5 drop-shadow-sm" />
                  {checkingIdx === img.id && (
                    <div className="absolute top-1 right-1 bg-primary text-white h-3.5 w-3.5 rounded-full flex items-center justify-center shadow-sm">
                      <CheckCircle2 className="h-2 w-2" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex-1 px-8 pt-2 pb-8 overflow-y-auto flex flex-col items-center">
            {/* Main Inspector Card */}
            {currentCheckingImage ? (
              <div className="w-full h-full flex flex-col items-center max-w-5xl">
                <div className="w-full bg-white dark:bg-[#0a0a0a] rounded-[2.5rem] shadow-xl overflow-hidden border flex flex-col h-[70vh] relative">
                  <div className="flex-1 bg-white relative flex items-center justify-center p-4">
                    {currentCheckingImage.status === 'done' ? (
                      <CompareSlider
                        before={currentCheckingImage.originalUrl}
                        after={currentCheckingImage.processedUrl!}
                      />
                    ) : (
                      <div className="relative w-full h-full flex items-center justify-center">
                        <img
                          src={currentCheckingImage.originalUrl}
                          className="max-h-full max-w-full object-contain opacity-50 blur-sm transition-all duration-500"
                        />
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-10">
                          <Loader2 className="h-10 w-10 animate-spin text-primary" />
                          <span className="text-[10px] font-black uppercase tracking-widest bg-white/80 px-3 py-1 rounded-full backdrop-blur-md shadow-sm">{t('inspector.rendering')}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Floating Control Center */}
            <div className={`fixed bottom-[10px] left-1/2 -translate-x-1/2 z-[100] transition-opacity duration-200 scale-90 md:scale-100 ${images.length > 0 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
              <Card className="p-1 rounded-full border shadow-2xl bg-white/95 dark:bg-black/95 backdrop-blur-lg flex items-center gap-1.5">
                {/* Presets Group */}
                <div className="flex items-center gap-4 px-4 py-1.5 bg-muted/5 rounded-full border-r pr-6">
                  <Tabs value={batchBgType} onValueChange={(v) => setBatchBgType(v as any)} className="w-auto">
                    <TabsList className="bg-transparent p-0 h-8 gap-1">
                      <TabsTrigger value="transparent" className="rounded-full px-4 text-[9px] font-black uppercase tracking-widest h-8 data-[state=active]:bg-primary data-[state=active]:text-white">{t('controls.transparent')}</TabsTrigger>
                      <TabsTrigger value="color" className="rounded-full px-4 text-[9px] font-black uppercase tracking-widest h-8 data-[state=active]:bg-primary data-[state=active]:text-white">{t('controls.color')}</TabsTrigger>
                      <TabsTrigger value="image" className="rounded-full px-4 text-[9px] font-black uppercase tracking-widest h-8 data-[state=active]:bg-primary data-[state=active]:text-white">{t('controls.scene')}</TabsTrigger>
                    </TabsList>
                  </Tabs>

                  {batchBgType === 'color' && (
                    <div className="flex gap-1">
                      {predefinedColors.slice(0, 5).map(color => (
                        <div key={color} onClick={() => setBatchBgColor(color)} className={`h-6 w-6 rounded-full border-2 cursor-pointer transition-all ${batchBgColor === color ? 'border-primary ring-2 ring-primary/20' : 'hover:scale-105 opacity-60'}`} style={{ backgroundColor: color }} />
                      ))}
                    </div>
                  )}

                  {batchBgType === 'image' && (
                    <div className="relative h-8 px-4 border rounded-full flex items-center gap-2 bg-white cursor-pointer hover:bg-muted/50 transition-colors">
                      <ImageIcon className="h-3 w-3 text-primary" />
                      <span className="text-[9px] font-black uppercase truncate max-w-[80px]">{batchBgImage ? batchBgImage.name : t('controls.upload_scene')}</span>
                      <input type="file" accept="image/*" onChange={(e) => setBatchBgImage(e.target.files?.[0] || null)} className="absolute inset-0 opacity-0 cursor-pointer" />
                    </div>
                  )}

                  <div className="flex items-center gap-3 w-32 ml-2">
                    <Slider value={[batchBlur]} onValueChange={(v) => setBatchBlur(v[0])} max={100} className="flex-1 py-1" />
                    <span className="text-[8px] font-black text-primary w-4">{batchBlur}</span>
                  </div>
                </div>

                {/* Action Group (Moved from Footer) */}
                <div className="flex items-center gap-2 px-2">
                  {currentCheckingImage && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => handleReprocess(currentCheckingImage.id)} className="h-9 w-9 rounded-full border hover:bg-primary/5 hover:text-primary transition-all" title={t('actions.reprocess')}>
                        <RefreshCw className="h-3.5 w-3.5" />
                      </Button>
                      <Button onClick={() => handleApprove(currentCheckingImage.id)} disabled={currentCheckingImage.status !== 'done'} className="h-8 px-6 rounded-full text-[9px] font-black uppercase tracking-widest gap-2 bg-primary shadow-lg shadow-primary/20 hover:scale-105 transition-all text-white ml-2">
                        <CheckCircle2 className="h-3.5 w-3.5" /> {t('actions.approve')}
                      </Button>
                    </>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </main>

        {/* Right Sidebar: Approved */}
        <aside className="w-[300px] border-l bg-white dark:bg-[#0a0a0a] flex flex-col shrink-0">
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
              <span className="text-[10px] font-black uppercase tracking-widest text-primary">{t('actions.export_zip')} • {images.filter(i => i.status === 'approved').length}</span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {images.filter(img => img.status === 'approved').length === 0 && (
              <div className="h-40 border-2 border-dashed rounded-[2rem] flex flex-col items-center justify-center p-6 opacity-20 gap-3">
                <Download className="h-8 w-8" />
                <p className="text-[10px] font-black uppercase tracking-widest text-center leading-relaxed">{t('upload.pipeline_empty')}</p>
              </div>
            )}

            {images.filter(img => img.status === 'approved').map(img => (
              <SidebarItem
                key={img.id}
                image={img}
                variant="approved"
                actions={
                  <>
                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full hover:bg-primary/10 hover:text-primary" onClick={() => handleReprocess(img.id)} title={t('actions.return_to_queue')}>
                      <RefreshCw className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full hover:bg-primary/10 hover:text-primary" asChild>
                      <a href={img.processedUrl} download={img.processedFile?.name}>
                        <Download className="h-3 w-3" />
                      </a>
                    </Button>
                  </>
                }
              />
            ))}
          </div>

          <div className="p-6 border-t bg-muted/5">
            <Button
              disabled={images.filter(i => i.status === 'approved').length === 0}
              onClick={downloadAllAsZip}
              className="w-full h-14 rounded-[1.5rem] font-black text-[11px] uppercase tracking-[0.2em] bg-primary hover:bg-primary/90 shadow-2xl shadow-primary/30 transition-all hover:scale-[1.02] active:scale-[0.98] text-white"
            >
              <Download className="h-4 w-4 mr-3" /> {t('actions.export_zip')}
            </Button>
          </div>
        </aside>
      </div>

      {
        currentCheckingImage && (
          <EditModal
            image={currentCheckingImage}
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            onSave={(newUrl) => {
              setImages(prev => prev.map(img =>
                img.id === currentCheckingImage.id ? { ...img, processedUrl: newUrl } : img
              ));
              setIsEditModalOpen(false);
            }}
          />
        )
      }
    </div >
  );
}
